<script setup lang="ts">
  import { SelectInput, IconImage } from '@/shared/ui';
  import AddButton from '@/shared/assets/icons/AddButton.svg';
  import DeleteButton from '@/shared/assets/icons/DeleteButton.svg';
  import type { Option, PositionRow } from '@/pages/admin/lib/useEditMemberForm';

  defineProps<{
    positions: PositionRow[];
    departmentOptions: Option[];
    isLoadingDepartments: boolean;
    canAddPosition: boolean;
    getPositionOptions: (departmentId: string | number, currentPositionId?: string | number) => Option[];
    isDepartmentLoadingPositions: (departmentId: string | number) => boolean;
  }>();

  const emit = defineEmits<{
    updateDepartment: [index: number, departmentId: string | number];
    updatePosition: [index: number, positionId: string | number];
    addPosition: [];
    removePosition: [index: number];
  }>();
</script>

<template>
  <div class="flex flex-shrink-0 flex-col space-y-4">
    <div
      v-for="(position, index) in positions"
      :key="position._key"
      class="flex gap-4 items-end"
    >
      <div class="w-64 flex flex-col gap-2">
        <span
          v-if="index === 0"
          class="text-sm text-gray-07 font-semibold"
        >
          Departamento:
        </span>
        <SelectInput
          :model-value="position.departmentId"
          :options="departmentOptions"
          :disabled="isLoadingDepartments"
          :label="isLoadingDepartments ? 'Carregando...' : 'Selecione um Departamento'"
          container-class="w-full"
          :use-teleport="true"
          @update:model-value="emit('updateDepartment', index, $event)"
        />
      </div>
      <div class="flex-1 flex flex-col gap-2">
        <span
          v-if="index === 0"
          class="text-sm text-gray-07 font-semibold"
        >
          Cargo:
        </span>
        <SelectInput
          :model-value="position.positionId"
          :options="getPositionOptions(position.departmentId, position.positionId)"
          :disabled="!position.departmentId || isDepartmentLoadingPositions(position.departmentId) || getPositionOptions(position.departmentId, position.positionId).length === 0"
          :label="isDepartmentLoadingPositions(position.departmentId) ? 'Carregando...' : 'Selecione um Cargo'"
          container-class="w-full"
          :use-teleport="true"
          @update:model-value="emit('updatePosition', index, $event)"
        />
      </div>
      <div class="flex items-center h-[42px]">
        <IconImage
          v-if="index === 0"
          :src="AddButton"
          alt="Adicionar cargo"
          :class="['text-[var(--theme-secondary-ink)]', canAddPosition ? 'cursor-pointer hover:opacity-80' : 'opacity-30 cursor-not-allowed']"
          @click="canAddPosition && emit('addPosition')"
        />
        <IconImage
          v-else
          :src="DeleteButton"
          alt="Remover cargo"
          class="cursor-pointer text-gray-05 hover:text-gray-07"
          @click="emit('removePosition', index)"
        />
      </div>
    </div>
  </div>
</template>
